import { ApiSuccess } from "./baseService";
import { getApiCall, postFormApiCall, postApiCall } from "./tokenService";
import { VehicleItems } from "../models/vehicle/vehicleItems";
import { VehicleEdit } from "../models/vehicle/vehicleEdit";
import { ApiResponse, apiCall } from "./apiClient";
import {
    VehiclePositionBatchDto,
    VehiclePositionBatchQueryParams,
    VehiclePositionInfo,
    VehiclePositionQueryParams,
} from "../models/vehicle/vehiclePosition";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { VehicleTripSegment } from "../models/vehicle/vehicleTripSegment";
import {
    VehicleEventInfo,
    VehicleEventQueryParams,
} from "../models/vehicle/vehicleEventInfo";
import {
    VehicleShiftDto,
    VehicleShiftListItemDto,
} from "../models/vehicleShift/vehicleShift";

export const getVehicleItems = (
    pageSize: number,
    page: number,
    filter: string,
    sortColumn: string,
    sortOrderIsAsc: boolean,
    showNotInUse: boolean
): Promise<VehicleItems> => {
    return getApiCall<VehicleItems>(
        "api/vehicle/?page=" +
            (page || "") +
            "&pageSize=" +
            (pageSize || "") +
            "&sortColumn=" +
            (sortColumn ? encodeURIComponent(sortColumn) : "") +
            "&sortOrder=" +
            (sortOrderIsAsc ? "asc" : "desc") +
            "&filter=" +
            (filter ? encodeURIComponent(filter) : "") +
            "&showNotInUse=" +
            (showNotInUse ? "1" : "0"),
        VehicleItems
    );
};

export const getVehicleEdit = (id: string): Promise<VehicleEdit> => {
    return getApiCall<VehicleEdit>(
        "api/vehicle/edit/?id=" + (id || ""),
        VehicleEdit
    );
};

export const saveVehicleEdit = (data: FormData): Promise<ApiSuccess> => {
    return postFormApiCall<ApiSuccess>("api/vehicle/save", data, ApiSuccess);
};

export const removeVehicle = (id: string): Promise<ApiSuccess> => {
    return postApiCall<ApiSuccess>(
        "api/vehicle/remove/?id=" + (id || ""),
        ApiSuccess
    );
};

export const getVehiclePosition = (
    vehicleId: string,
    windowStartTime?: string,
    windowEndTime?: string,
    signal?: AbortSignal
): Promise<ApiResponse<VehiclePositionInfo[]>> => {
    return apiCall<VehiclePositionInfo[], VehiclePositionQueryParams>(
        `vehicles/${vehicleId}/position`,
        "GET",
        { windowStartTime, windowEndTime },
        signal
    );
};

export const getVehiclePositionsBatch = (
    params: VehiclePositionBatchQueryParams[],
    signal?: AbortSignal
): Promise<ApiResponse<VehiclePositionBatchDto>> => {
    return apiCall<VehiclePositionBatchDto, VehiclePositionBatchQueryParams[]>(
        "vehicles/positions/batch",
        "POST",
        params,
        signal
    );
};

export type VehicleDistanceAndFuelQueryPrototypes = (
    vehicleIds: string[],
    windowStartTime: string,
    windowEndTime: string,
    groupByWorkShiftTimeslots: boolean,
    signal?: AbortSignal
) => Promise<ApiResponse<VehicleEventInfo[]>>;

export const getVehicleDistance = (
    vehicleIds: string[],
    windowStartTime: string,
    windowEndTime: string,
    groupByWorkShiftTimeslots: boolean,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleEventInfo[]>> => {
    return apiCall<VehicleEventInfo[], VehicleEventQueryParams>(
        "vehicles/distance",
        "POST",
        {
            vehicleIds,
            windowStartTime,
            windowEndTime,
            groupByWorkShiftTimeslots,
        },
        signal
    );
};

export const getVehicleTotalFuel = (
    vehicleIds: string[],
    windowStartTime: string,
    windowEndTime: string,
    groupByWorkShiftTimeslots: boolean,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleEventInfo[]>> => {
    return apiCall<VehicleEventInfo[], VehicleEventQueryParams>(
        "vehicles/fuel",
        "POST",
        {
            vehicleIds,
            windowStartTime,
            windowEndTime,
            groupByWorkShiftTimeslots,
        },
        signal
    );
};

export const getVehiclePositions = (
    vehicleIds?: string[],
    includeWorkTimeTypes = false,
    signal?: AbortSignal
): Promise<ApiResponse<VehiclePositionInfo[]>> => {
    return apiCall<VehiclePositionInfo[]>(
        `vehicles/positions?includeWorkTimeTypes=${encodeURIComponent(
            includeWorkTimeTypes
        )}&${(vehicleIds || [])
            .map((id) => `&vehicleIds=${encodeURIComponent(id)}`)
            .join("")}`,
        "GET",
        null,
        signal
    );
};

export const getVehicles = (
    includeInactive = false,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleListItemDto[]>> => {
    return apiCall<VehicleListItemDto[]>(
        `vehicles?includeInactive=${encodeURIComponent(includeInactive)}`,
        "GET",
        null,
        signal
    );
};

export const getVehicleShifts = (
    vehicleId: string,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleShiftListItemDto[]>> => {
    return apiCall<VehicleShiftListItemDto[]>(
        `vehicleshifts?vehicleId=${encodeURIComponent(vehicleId)}`,
        "GET",
        null,
        signal
    );
};

export const getCurrentVehicleShift = (
    vehicleId: string,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleShiftDto>> => {
    return apiCall<VehicleShiftDto>(
        `vehicleshifts/current?vehicleId=${encodeURIComponent(vehicleId)}`,
        "GET",
        null,
        signal
    );
};

export const getVehicleShift = (
    vehicleShiftId: string,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleShiftDto>> => {
    return apiCall<VehicleShiftDto>(
        `vehicleshifts/${encodeURIComponent(vehicleShiftId)}`,
        "GET",
        null,
        signal
    );
};

export const getVehicleTripSegments = (
    vehicleId: string,
    windowStartTime: string,
    windowEndTime: string,
    useGeocoding = true,
    signal?: AbortSignal
): Promise<ApiResponse<VehicleTripSegment[]>> => {
    return apiCall<VehicleTripSegment[]>(
        `vehicles/${vehicleId}/tripsegments?windowStartTime=${encodeURIComponent(windowStartTime)}&windowEndTime=${encodeURIComponent(windowEndTime)}&useGeocoding=${encodeURIComponent(useGeocoding)}`,
        "GET",
        null,
        signal
    );
};
